import { FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  IdentityA11yAnnouncer,
  IdentityError as IdentityErrorView,
  IdentityFooterInline,
  IdentityForm,
  IdentityFormData,
  IdentityHero,
  IdentityLayout,
} from "@speedycore/identity-ui";
import { identityRegister, oauthClientIdForRedirect, redirectAfterAuth, sendIdentityTelemetry } from "../lib/identityApi";
import { AiAcknowledgement } from "../components/AiAcknowledgement";

const SIGNUP_BULLETS = [
  "One account for all hubs",
  "Secure RS256 identity",
  "No algorithm, no tracking, no manipulation",
] as const;

export function SignupPage() {
  const [params] = useSearchParams();
  const redirectUri = useMemo(() => params.get("redirect_uri"), [params]);
  const oauthClientId = useMemo(() => oauthClientIdForRedirect(redirectUri), [redirectUri]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [a11y, setA11y] = useState<string>("");
  const [exists, setExists] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [aiAccepted, setAiAccepted] = useState(false);

  const loginHref = redirectUri
    ? `/auth/login?redirect_uri=${encodeURIComponent(redirectUri)}`
    : "/auth/login";

  async function onSubmit(data: IdentityFormData) {
    setError(null);
    setExists(false);
    if (data.confirmPassword !== undefined && data.password !== data.confirmPassword) {
      const m = "Passwords do not match";
      setError(m);
      setA11y(m);
      return;
    }
    if (data.password.length < 8) {
      const m = "Password too short";
      setError(m);
      setA11y(m);
      return;
    }
    if (!legalAccepted) {
      const m = "You must accept the Terms & Conditions and Privacy Policy to continue.";
      setError(m);
      setA11y(m);
      return;
    }
    if (!aiAccepted) {
      const m = "Please acknowledge the AI accuracy responsibility notice to continue.";
      setError(m);
      setA11y(m);
      return;
    }
    setBusy(true);
    try {
      const result = await identityRegister(data.email, data.password, undefined, oauthClientId, legalAccepted, undefined, undefined, aiAccepted);
      redirectAfterAuth(redirectUri, result);
    } catch (err) {
      const code = (err as Error & { code?: string }).code;
      if (code === "ACCOUNT_ALREADY_EXISTS") {
        setExists(true);
        setError("Email already in use");
      } else {
        setError(err instanceof Error ? err.message : "Sign up failed.");
      }
      setBusy(false);
      void sendIdentityTelemetry("identity.signup_failed", {
        error_code: code || "signup_failed",
      });
    }
  }

  return (
    <>
      <IdentityA11yAnnouncer message={a11y} type="error" />
      <IdentityLayout
        hero={
          <IdentityHero
            title="Create your SpeedyCore account"
            subtitle="Your identity across all SpeedyCore sites."
            bullets={SIGNUP_BULLETS}
          />
        }
        form={
          <>
            <IdentityForm
              mode="signup"
              onSubmit={onSubmit}
              loading={busy}
              errors={error ? [{ message: error }] : undefined}
              submitLabel="Create SpeedyCore account"
            />
            <div style={{ marginTop: "0.75rem", marginBottom: "0.5rem" }}>
              <label style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", fontSize: "0.85rem", color: "#1f2937", cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  id="speedycore-legal-consent"
                  name="speedycore_legal_consent"
                  checked={legalAccepted}
                  onChange={(e) => setLegalAccepted(e.target.checked)}
                  style={{ marginTop: "0.2rem", transform: "scale(1.05)" }}
                  aria-describedby="speedycore-legal-text"
                  required
                />
                <span id="speedycore-legal-text" style={{ lineHeight: 1.5 }}>
                  I confirm I am at least <strong>18 years old</strong>, or the age of majority in my jurisdiction,
                  and I agree to the Speedy Systems Pty Ltd{" "}
                  <Link to="/legal/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#1e3a8a", textDecoration: "underline" }}>Terms &amp; Conditions</Link>{" "}and{" "}
                  <Link to="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#1e3a8a", textDecoration: "underline" }}>Privacy Policy</Link>.
                </span>
              </label>
            </div>
            <AiAcknowledgement checked={aiAccepted} onChange={setAiAccepted} />
            {exists && error ? (
              <div style={{ marginTop: "0.25rem" }}>
                <IdentityErrorView message="Email already in use" />
                <p className="sc-identity-links">
                  <Link to={loginHref}>Already have an account?</Link>
                </p>
              </div>
            ) : (
              <div className="sc-identity-links">
                <span />
                <Link to={loginHref}>Already have an account?</Link>
              </div>
            )}
            <IdentityFooterInline />
          </>
        }
      />
    </>
  );
}
